/* eslint no-console: ["error", { allow: ["error", "warn"] }] */
/**
 * Shadow of @plone/volto-slate/utils/blocks.js
 *
 * Fix: toggleInlineFormat now uses rangeRef when wrapping to track selection
 * through Slate normalization (which adds empty text nodes around inline elements).
 */
import { Editor, Transforms, Text } from 'slate'; // Range, RangeRef
import config from '@plone/volto/registry';
import {
  getBlocksFieldname,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers/Blocks/Blocks';
import find from 'lodash/find';
import includes from 'lodash/includes';
import some from 'lodash/some';
import first from 'lodash/first';
import { makeEditor } from '@plone/volto-slate/utils/editor';
import { safeEditorNodes } from '@plone/volto-slate/utils/safe';

// case sensitive; first in an inner array is the default and preffered format
// in that array of formats
const formatAliases = [
  ['strong', 'b'],
  ['em', 'i'],
  ['del', 's'],
];

export const normalizeExternalData = (editor, nodes) => {
  let fakeEditor = makeEditor({ extensions: editor._installedPlugins });
  fakeEditor.children = nodes;
  // put all the non-blocks (e.g. images which are inline Elements) inside p-s
  Editor.withoutNormalizing(fakeEditor, () => {
    //for htmlSlateWidget compatibility
    if (
      nodes &&
      (!Editor.isBlock(fakeEditor, nodes[0]) || Text.isText(nodes[0]))
    )
      Transforms.wrapNodes(
        fakeEditor,
        { type: 'p' },
        {
          at: [],
          match: (node, path) =>
            (!Editor.isEditor(node) && !Editor.isBlock(fakeEditor, node)) ||
            fakeEditor.isInline(node) ||
            Text.isText(node),
          mode: 'highest',
        },
      );
  });

  Editor.normalize(fakeEditor, { force: true });

  return fakeEditor.children;
};

/**
 * Is it text? Is it whitespace (space, newlines, tabs) ?
 *
 */
export const isWhitespace = (c) => {
  return (
    typeof c === 'string' &&
    c.replace(/\s/g, '').replace(/\t/g, '').replace(/\n/g, '').length === 0
  );
};

export function createDefaultBlock(children) {
  return {
    type: config.settings.slate.defaultBlockType,
    children: children || [{ text: '' }],
  };
}

export function createBlock(type, children) {
  return {
    type,
    children: children || [{ text: '' }],
  };
}

export function createEmptyParagraph() {
  // TODO: rename to createEmptyBlock
  return {
    type: config.settings.slate.defaultBlockType,
    children: [{ text: '' }],
  };
}

export function createParagraph(text) {
  return {
    type: config.settings.slate.defaultBlockType,
    children: [{ text }],
  };
}

export const isSingleBlockTypeActive = (editor, format) => {
  const [match] = safeEditorNodes(editor, {
    match: (n) => n.type === format,
  });

  return !!match;
};

export const isBlockActive = (editor, format) => {
  const aliasList = find(formatAliases, (x) => includes(x, format));

  if (aliasList) {
    const aliasFound = some(aliasList, (y) => {
      return isSingleBlockTypeActive(editor, y);
    });

    if (aliasFound) {
      return true;
    }
  }

  return isSingleBlockTypeActive(editor, format);
};

export const getBlockTypeContextData = (editor, format) => {
  let isActive, defaultFormat, matcher;

  const aliasList = find(formatAliases, (x) => includes(x, format));

  if (aliasList) {
    const aliasFound = some(aliasList, (y) => {
      return isSingleBlockTypeActive(editor, y);
    });

    if (aliasFound) {
      isActive = true;
      defaultFormat = first(aliasList);
      matcher = (n) => includes(aliasList, n.type);

      return { isActive, defaultFormat, matcher };
    }
  }

  isActive = isBlockActive(editor, format);
  defaultFormat = format;
  matcher = (n) => n.type === format;

  return { isActive, defaultFormat, matcher };
};

export const toggleInlineFormat = (editor, format) => {
  if (!editor.selection) {
    return;
  }

  const { isActive, defaultFormat, matcher } = getBlockTypeContextData(
    editor,
    format,
  );

  if (isActive) {
    const rangeRef = Editor.rangeRef(editor, editor.selection);

    Transforms.unwrapNodes(editor, {
      match: matcher,
      split: false,
    });

    if (rangeRef.current) {
      const newSel = JSON.parse(JSON.stringify(rangeRef.current));
      rangeRef.unref();
      Transforms.select(editor, newSel);
      if (editor.setSavedSelection) {
        editor.setSavedSelection(newSel);
      }
    } else {
      rangeRef.unref();
    }
    return;
  }

  const exclusiveElements = config.settings.slate.exclusiveElements;
  const matchedElements = exclusiveTags(exclusiveElements, format);
  let alreadyOneIsActive =
    !!matchedElements &&
    (matchedElements.indexOf(format) === 0
      ? isBlockActive(editor, matchedElements[1])
      : isBlockActive(editor, matchedElements[0]));

  if (alreadyOneIsActive) {
    // Track selection through wrap/unwrap transforms - normalization adds
    // empty text nodes around inline elements, which shifts paths
    const rangeRef = Editor.rangeRef(editor, editor.selection);

    Transforms.unwrapNodes(editor, {
      match: (n) => matchedElements.includes(n.type),
      split: false,
    });

    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block, { split: true });

    // Restore selection from tracked range
    if (rangeRef.current) {
      const newSel = JSON.parse(JSON.stringify(rangeRef.current));
      rangeRef.unref();
      Transforms.select(editor, newSel);
      if (editor.setSavedSelection) {
        editor.setSavedSelection(newSel);
      }
    } else {
      rangeRef.unref();
    }
    return;
  }

  // `children` property is added automatically as an empty array then
  // normalized
  // Track selection through wrapNodes - normalization adds empty text nodes
  // around inline elements (like <strong>), which shifts paths
  const rangeRef = Editor.rangeRef(editor, editor.selection);
  const block = { type: defaultFormat };
  Transforms.wrapNodes(editor, block, { split: true });

  // Restore selection from tracked range
  if (rangeRef.current) {
    const newSel = JSON.parse(JSON.stringify(rangeRef.current));
    rangeRef.unref();
    Transforms.select(editor, newSel);
    if (editor.setSavedSelection) {
      editor.setSavedSelection(newSel);
    }
  } else {
    rangeRef.unref();
  }
};

const exclusiveTags = (exclusiveElements, format) => {
  let elements = null;
  for (const item of exclusiveElements) {
    if (item.includes(format)) {
      elements = item;
      break;
    }
  }

  return elements;
};

export const toggleBlock = (editor, format, allowedChildren) => {
  // We have 6 boolean variables which need to be accounted for.
  // See https://docs.google.com/spreadsheets/d/1mVeMuqSTMABV2BhoHPrPAFjn7zUksbNgZ9AQK_dcd3U/edit?usp=sharing
  const { slate } = config.settings;
  const { listTypes } = slate;

  const isListItem = isBlockActive(editor, slate.listItemType);
  const isActive = isBlockActive(editor, format);
  const wantsList = listTypes.includes(format);

  if (isListItem && !wantsList) {
    toggleFormatAsListItem(editor, format);
  } else if (isListItem && wantsList && !isActive) {
    switchListType(editor, format);
  } else if (!isListItem && wantsList) {
    changeBlockToList(editor, format);
  } else if (!isListItem && !wantsList) {
    toggleFormat(editor, format, allowedChildren);
  } else if (isListItem && wantsList && isActive) {
    clearList(editor);
  } else {
    console.warn('toggleBlock case not covered, please examine:', {
      wantsList,
      isActive,
      isListItem,
    });
  }
};

/*
 * Applies a block format to a list item. Will split the list
 */
export const toggleFormatAsListItem = (editor, format) => {
  Transforms.setNodes(editor, {
    type: format,
  });

  Editor.normalize(editor);
};

/*
 * Toggles between list types by exploding the block
 */
export const switchListType = (editor, format) => {
  const { slate } = config.settings;
  Transforms.unwrapNodes(editor, {
    match: (n) => slate.listTypes.includes(n.type),
    split: true,
  });
  const block = { type: format, children: [] };
  Transforms.wrapNodes(editor, block);
};

/*
 * Clear list by exploding the block
 */
export const clearList = (editor) => {
  const { slate } = config.settings;
  Transforms.unwrapNodes(editor, {
    match: (n) => slate.listTypes.includes(n.type),
    split: true,
  });
  Transforms.setNodes(editor, {
    type: 'p',
  });
  Editor.normalize(editor);
};

export const changeBlockToList = (editor, format) => {
  const { slate } = config.settings;
  const [match] = Editor.nodes(editor, {
    match: (n) => n.type === slate.listItemType,
  });

  if (!match) {
    Transforms.setNodes(editor, {
      type: slate.listItemType,
      // id: nanoid(8),
    });
  }

  // `children` property is added automatically as an empty array then
  // normalized
  const block = { type: format };
  Transforms.wrapNodes(editor, block);
};

export const toggleFormat = (editor, format, allowedChildren) => {
  const { slate } = config.settings;
  const isActive = isBlockActive(editor, format);
  const type = isActive ? slate.defaultBlockType : format;
  Transforms.setNodes(editor, {
    type,
  });
  allowedChildren?.length &&
    Transforms.unwrapNodes(editor, {
      mode: 'all',
      at: [0],
      match: (n, path) => {
        const isMatch =
          path.length > 1 && // we don't deal with the parent nodes
          !(Text.isText(n) || allowedChildren.includes(n?.type));
        return isMatch;
      },
    });
};

/**
 * @param {object} properties A prop received by the View component
 *  which is read by the `getBlocksFieldname` and
 * `getBlocksLayoutFieldname` Volto helpers to produce the return value.
 * @returns {Array} All the blocks data taken from the Volto form.
 */
export const getAllBlocks = (properties, blocks) => {
  const blocksFieldName = getBlocksFieldname(properties);
  const blocksLayoutFieldname = getBlocksLayoutFieldname(properties);

  for (const n of properties?.[blocksLayoutFieldname]?.items || []) {
    const block = properties[blocksFieldName][n];
    // TODO Make this configurable via block config getBlocks
    if (
      block?.data?.[blocksLayoutFieldname] &&
      block?.data?.[blocksFieldName]
    ) {
      getAllBlocks(block.data, blocks);
    } else if (block?.[blocksLayoutFieldname] && block?.[blocksFieldName]) {
      getAllBlocks(block, blocks);
    }
    blocks.push({
      id: n,
      ...block,
    });
  }
  return blocks;
};

export const clearFormatting = (editor) => {
  const { slate } = config.settings;
  Transforms.setNodes(editor, {
    type: slate.defaultBlockType,
  });
  Transforms.unwrapNodes(editor, {
    match: (n) => n.type && n.type !== slate.defaultBlockType,
    mode: 'all',
    split: false,
  });
};
